import { Condition, ConditionFeatureState } from '@wix/add-panel-common';
import type { ConditionHelpers } from '../../UtilsProvider/UtilsProvider.types';

export type EvaluateCondition = (condition: Condition) => boolean;

export const evaluateCondition = (
  condition: Condition,
  helpers: ConditionHelpers,
  kind: 'add-panel' | 'design-panel',
): boolean => {
  if (kind === 'design-panel' && condition.ignoreDesignPanel === true) {
    return true;
  }
  const {
    isRepeaterItem,
    isTPAInstalled,
    isMembersAppInstalled,
    isSuperAppInstalled,
    isDevModeOn,
    isDataModeOn,
    isProvisionedOn,
    isMultilingualModeOn,
    isEducationModeOn,
    isInteractionModeOn,
    isLightboxModeOn,
    isFullWidthLightboxModeOn,
    hasSavedComponents,
    hasMyWidgets,
    isRefComponentSupported,
    isStaticPagesOverLimit,
    isConnectablePage,
    isCustomMenuEnabled,
    hasCustomTheme,
    isAdminPage,
  } = helpers;

  const evaluatedConditions = [];
  // is admin page
  const { adminPage } = condition;
  if (adminPage) {
    evaluatedConditions.push(isAdminPage() === (adminPage === ConditionFeatureState.On));
  }

  // currently selected item on stage is repeater or its child
  const { repeaterItem } = condition;
  if (repeaterItem) {
    evaluatedConditions.push(isRepeaterItem() === (repeaterItem === ConditionFeatureState.On));
  }

  // page is connectable
  const { pageIsConnectable } = condition;
  if (pageIsConnectable) {
    evaluatedConditions.push(isConnectablePage() === (pageIsConnectable === ConditionFeatureState.On));
  }

  // can add static page
  const { staticPagesOverLimit } = condition;
  if (staticPagesOverLimit) {
    evaluatedConditions.push(isStaticPagesOverLimit() === (staticPagesOverLimit === ConditionFeatureState.On));
  }

  // is ref component supported
  const { refComponentSupported } = condition;
  if (refComponentSupported) {
    evaluatedConditions.push(isRefComponentSupported() === (refComponentSupported === ConditionFeatureState.On));
  }

  // is members installed
  const { membersInstalled } = condition;
  if (membersInstalled) {
    evaluatedConditions.push(isMembersAppInstalled() === (membersInstalled === ConditionFeatureState.On));
  }

  // is TPA installed
  const { tpaInstalled } = condition;
  if (tpaInstalled?.id) {
    evaluatedConditions.push(isTPAInstalled(tpaInstalled.id) === Boolean(tpaInstalled.isInstalled));
  }

  // is SuperApp installed
  const { superAppInstalled } = condition;
  if (superAppInstalled?.id) {
    evaluatedConditions.push(isSuperAppInstalled(superAppInstalled.id) === Boolean(superAppInstalled.isInstalled));
  }

  // is data mode on
  const { dataMode } = condition;
  if (dataMode) {
    evaluatedConditions.push(isDataModeOn() === (dataMode === ConditionFeatureState.On));
  }

  // is dev mode on
  const { devMode } = condition;
  if (devMode) {
    evaluatedConditions.push(isDevModeOn() === (devMode === ConditionFeatureState.On));
  }

  // is provisioned on
  const { provisioned } = condition;
  if (provisioned) {
    evaluatedConditions.push(isProvisionedOn() === (provisioned === ConditionFeatureState.On));
  }

  // is multilingual mode on
  const { multilingualMode } = condition;
  if (multilingualMode) {
    evaluatedConditions.push(isMultilingualModeOn() === (multilingualMode === ConditionFeatureState.On));
  }

  // is education mode on
  const { educationMode } = condition;
  if (educationMode) {
    evaluatedConditions.push(isEducationModeOn() === (educationMode === ConditionFeatureState.On));
  }

  // is interaction mode on
  const { interactionMode } = condition;
  if (interactionMode) {
    evaluatedConditions.push(isInteractionModeOn() === (interactionMode === ConditionFeatureState.On));
  }

  // is lightbox mode on
  const { lightboxMode } = condition;
  if (lightboxMode) {
    evaluatedConditions.push(isLightboxModeOn() === (lightboxMode === ConditionFeatureState.On));
  }

  // is full width lightbox mode on
  const { fullWidthLightboxMode } = condition;
  if (fullWidthLightboxMode) {
    evaluatedConditions.push(isFullWidthLightboxModeOn() === (fullWidthLightboxMode === ConditionFeatureState.On));
  }

  // has my designs
  const { myDesigns } = condition;
  if (myDesigns) {
    evaluatedConditions.push(hasSavedComponents() === (myDesigns === ConditionFeatureState.On));
  }

  // has my widgets
  const { myWidgets: myWidgetsCondition } = condition;
  if (myWidgetsCondition) {
    evaluatedConditions.push(hasMyWidgets() === (myWidgetsCondition === ConditionFeatureState.On));
  }

  //has custom menu
  const { customMenuEnabled } = condition;
  if (customMenuEnabled) {
    evaluatedConditions.push(isCustomMenuEnabled() === (customMenuEnabled === ConditionFeatureState.On));
  }

  // component  custom theme
  const { customTheme } = condition;
  if (customTheme) {
    evaluatedConditions.push(hasCustomTheme(customTheme.compType) === Boolean(customTheme.isWithCustomTheme));
  }

  return evaluatedConditions.every(Boolean);
};
